import { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface EditMessageDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    messageContent: string;
    onSave: (newContent: string) => void;
}

export function EditMessageDialog({ open, onOpenChange, messageContent, onSave }: EditMessageDialogProps) {
    const [editedContent, setEditedContent] = useState(messageContent);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setEditedContent(messageContent);
    }, [messageContent]);

    useEffect(() => {
        if (open && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
        }
    }, [open]);

    const handleSave = () => {
        if (editedContent.trim() !== "") {
            onSave(editedContent.trim());
            onOpenChange(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSave();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[525px]">
                <DialogHeader>
                    <DialogTitle>Edit Message</DialogTitle>
                    <DialogDescription>Make changes to your message. Press Ctrl+Enter to save.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <Textarea
                        ref={textareaRef}
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="min-h-[150px]"
                        placeholder="Enter your message..."
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={editedContent.trim() === ""}>
                        Save Changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
